import { useContext, useMemo, type FC } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { getCoreLevel } from '@wix/da-shared-react/pkg/publicSession/selectors';
import UserSymbol from '@wix/da-shared-react/pkg/User/_partials/UserSymbol/UserSymbol';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import {
  getVideoCreatorModel,
  getVideoCreatorRestrictions,
} from '../../../../selectors/studio';
import { VideoModel } from '../../../../types/store';
import { getModelPropertiesByType } from '../../ModelProperties';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';

import s from './CoreUpsell.scss';

export interface Props {}

const coreMembershipLink = Url.coreMembershipLink({
  referer: Url.videoCreatorLink(),
});

const CoreUpsell: FC<Props> = () => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const coreLevel = useSelector(getCoreLevel);
  const { videoPromptsRemaining } = useSelector(getVideoCreatorRestrictions);
  const selectedModel = useSelector(getVideoCreatorModel);

  const model = useMemo(() => {
    return getModelPropertiesByType(selectedModel);
  }, [selectedModel]);

  const isOutOfPrompts = useMemo(() => {
    if (!hasCoreMembership) {
      return videoPromptsRemaining < 1;
    } else {
      return false;
    }
  }, [hasCoreMembership, videoPromptsRemaining]);

  const showNonCorePromptsUpsell = useMemo(() => {
    if (hasCoreMembership) {
      return false;
    }
    if (model?.type !== VideoModel.HAILUO) {
      return false;
    }
    if (isOutOfPrompts) {
      return false;
    }
    return true;
  }, [hasCoreMembership, model?.type, isOutOfPrompts]);

  const showNonCoreUnlockUpsell = useMemo(() => {
    if (!hasCoreMembership && !showNonCorePromptsUpsell) {
      return true;
    } else {
      return false;
    }
  }, [hasCoreMembership, showNonCorePromptsUpsell]);

  return (
    <div className={s['root']}>
      <div className={s['alignment']}>
        {showNonCorePromptsUpsell && (
          <>
            <div className={s['upsell-part']}>
              {t('studio.videoCreator.coreUpsell.firstPart.nonCore')}
            </div>
            <div className={s['upsell-part']}>
              {t('studio.videoCreator.coreUpsell.secondPart.nonCore', {
                postProcess: 'reactPostprocessor',
                coreSymbol: (
                  <CoreSymbol
                    className={s['core-icon']}
                    size={IconSize.SMALL}
                  />
                ),
                upgradeLink: (
                  <PlainButtonOrLink
                    className={s['upgrade-link']}
                    href={coreMembershipLink}
                    target="_blank"
                    biData={BiData<BuyCoreButtonBiEvent>({
                      evid: 375,
                      referral_info: ReferralType.VIDEO_CREATOR_UPSELL,
                    })}
                  >
                    {t('studio.videoCreator.button.upgradeCore')}
                  </PlainButtonOrLink>
                ),
              })}
            </div>
          </>
        )}

        {showNonCoreUnlockUpsell && (
          <>
            {t('studio.videoCreator.coreUpsell.firstPart.nonCore.coreModel', {
              postProcess: 'reactPostprocessor',
              br: <br />,
              coreSymbol: (
                <CoreSymbol className={s['core-icon']} size={IconSize.SMALL} />
              ),
              upgradeLink: (
                <PlainButtonOrLink
                  className={s['upgrade-link']}
                  href={coreMembershipLink}
                  target="_blank"
                  biData={BiData<BuyCoreButtonBiEvent>({
                    evid: 375,
                    referral_info: ReferralType.VIDEO_CREATOR_UPSELL,
                  })}
                >
                  {t('studio.videoCreator.button.upgradeCore')}
                </PlainButtonOrLink>
              ),
            })}
          </>
        )}

        {coreLevel === 'core_plus' && (
          <>
            {t('studio.videoCreator.coreUpsell.firstPart.corePlus', {
              postProcess: 'reactPostprocessor',
              coreIcon: (
                <UserSymbol
                  userSymbol={coreLevel}
                  className={s['core-status']}
                />
              ),
            })}
            <br />
            {t('studio.videoCreator.coreUpsell.secondPart.corePlus', {
              postProcess: 'reactPostprocessor',
              coreSymbol: (
                <CoreSymbol className={s['core-icon']} size={IconSize.SMALL} />
              ),
              upgradeLink: (
                <PlainButtonOrLink
                  className={s['upgrade-link']}
                  href={coreMembershipLink}
                  target="_blank"
                  biData={BiData<BuyCoreButtonBiEvent>({
                    evid: 375,
                    referral_info: ReferralType.VIDEO_CREATOR_UPSELL,
                  })}
                >
                  {t('studio.videoCreator.coreUpsell.upgradeLink.core')}
                </PlainButtonOrLink>
              ),
            })}
          </>
        )}
        {(coreLevel === 'core_pro' || coreLevel === 'core_pro_plus') && (
          <>
            {t('studio.videoCreator.coreUpsell.firstPart.pro', {
              postProcess: 'reactPostprocessor',
              coreIcon: (
                <UserSymbol
                  userSymbol={coreLevel}
                  className={s['core-status']}
                />
              ),
              percentage: coreLevel === 'core_pro' ? 20 : 40,
            })}
            <br />
            {t('studio.videoCreator.coreUpsell.secondPart.pro', {
              postProcess: 'reactPostprocessor',
              coreSymbol: (
                <CoreSymbol className={s['core-icon']} size={IconSize.SMALL} />
              ),
              upgradeLink: (
                <PlainButtonOrLink
                  className={s['upgrade-link']}
                  href={coreMembershipLink}
                  target="_blank"
                  biData={BiData<BuyCoreButtonBiEvent>({
                    evid: 375,
                    referral_info: ReferralType.VIDEO_CREATOR_UPSELL,
                  })}
                >
                  {t('studio.videoCreator.coreUpsell.upgradeLink.core')}
                </PlainButtonOrLink>
              ),
            })}
          </>
        )}
        {coreLevel === 'core_max' &&
          t('studio.videoCreator.coreUpsell.firstPart.max', {
            postProcess: 'reactPostprocessor',
            coreIcon: (
              <UserSymbol userSymbol={coreLevel} className={s['core-status']} />
            ),
          })}
      </div>
    </div>
  );
};

CoreUpsell.displayName = 'CoreUpsell';
export default CoreUpsell;
